import { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { SignOut, GearSix, CaretDown, Clock, Warning } from "@phosphor-icons/react";
import { Mascot } from "@/components/styleguide/Mascot";
import { cn } from "@/lib/utils";
import { useAuth } from "./AuthContext";

/* ── Helpers ──────────────────────────────────────────────────────────── */
function getInitials(name = "") {
  return (
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .join("") || "?"
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function planLabel(plan) {
  if (!plan) return "Plan";
  if (plan === "monthly") return "Monthly Plan";
  if (plan === "annual")  return "Annual Plan";
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

/* ── Profile dropdown ─────────────────────────────────────────────────── */
function ProfileDropdown({ user, onSignOut, onSubscribe }) {
  const status      = user?.subscription_status;
  const isActive    = status === "active";
  const isTrialing  = status === "trialing";
  const isCancelled = status === "cancelled";
  const daysLeft    = user?.days_left ?? 0;
  const urgent      = isTrialing && daysLeft <= 3;
  const TRIAL_TOTAL = 14;
  const barPct = isTrialing
    ? Math.max(4, Math.round((daysLeft / TRIAL_TOTAL) * 100))
    : 0;

  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelling, setCancelling]       = useState(false);

  async function handleCancel() {
    setCancelling(true);
    try {
      await fetch("/api/billing/cancel", { method: "POST", credentials: "include" });
      window.location.reload();
    } catch {
      setCancelling(false);
      setConfirmCancel(false);
    }
  }

  return (
    <div className="absolute right-0 top-[calc(100%+6px)] z-50 w-[280px] overflow-hidden rounded-2xl border border-border bg-card shadow-soft-lg">

      {/* ── Identity header ── */}
      <div className="flex items-center gap-3 border-b border-border bg-muted/40 px-4 py-3.5">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-black text-primary-foreground">
          {getInitials(user?.name)}
        </div>
        <div className="min-w-0">
          <p className="truncate font-display text-sm font-black text-foreground">{user?.name}</p>
          <p className="truncate text-xs font-semibold text-muted-foreground">{user?.email}</p>
        </div>
      </div>

      {/* ── Plan section ── */}
      <div className="border-b border-border px-4 py-3.5">

        {/* Trial state */}
        {isTrialing && (
          <>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">
                Free Trial
              </span>
              <span
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold",
                  urgent
                    ? "bg-destructive/10 text-destructive"
                    : "bg-primary/10 text-primary-strong",
                )}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
                {urgent ? "Expiring soon" : "Trialing"}
              </span>
            </div>

            <div className="flex items-baseline gap-1.5">
              <span
                className={cn(
                  "font-display text-3xl font-black",
                  urgent ? "text-destructive" : "text-primary-strong",
                )}
              >
                {daysLeft}
              </span>
              <span className="text-sm font-semibold text-muted-foreground">
                day{daysLeft !== 1 ? "s" : ""} remaining
              </span>
            </div>

            <p className="mt-0.5 text-xs font-semibold text-muted-foreground">
              Ends{" "}
              <span className="font-bold text-foreground">{fmtDate(user?.trial_end)}</span>
            </p>

            {/* Progress bar */}
            <div className="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-border">
              <div
                className={cn(
                  "h-full rounded-full",
                  urgent ? "bg-destructive" : "bg-primary",
                )}
                style={{ width: `${barPct}%` }}
              />
            </div>

            {/* Upgrade CTA */}
            <button
              onClick={onSubscribe}
              className={cn(
                "mt-3 block w-full rounded-xl border-2 py-2 text-center text-xs font-extrabold transition",
                urgent
                  ? "border-destructive text-destructive hover:bg-destructive/10"
                  : "border-primary text-primary-strong hover:bg-primary/10",
              )}
            >
              Upgrade to a plan →
            </button>
          </>
        )}

        {/* Active plan state */}
        {isActive && (
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-display text-sm font-black text-foreground">
                {planLabel(user?.plan)}
              </p>
              <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-muted-foreground">
                <Clock size={11} weight="bold" />
                Renews {fmtDate(user?.stripe_subscription_period_end)}
              </p>
            </div>
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-teal/15 px-2.5 py-0.5 text-[11px] font-bold text-teal-ink">
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
              Active
            </span>
          </div>
        )}

        {/* Cancelled — still has access until period end */}
        {isCancelled && (
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-display text-sm font-black text-foreground">
                {planLabel(user?.plan)}
              </p>
              <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-muted-foreground">
                <Clock size={11} weight="bold" />
                Access until {fmtDate(user?.stripe_subscription_period_end)}
              </p>
            </div>
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-bold text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
              Cancelled
            </span>
          </div>
        )}

        {/* Fallback — expired / payment_failed / unknown */}
        {!isTrialing && !isActive && !isCancelled && (
          <p className="text-sm font-semibold text-muted-foreground">No active plan</p>
        )}
      </div>

      {/* ── Manage subscription — active or cancelled ── */}
      {(isActive || isCancelled) && (
        <div className="border-b border-border bg-muted/30 px-4 py-3.5">
          <p className="mb-2.5 text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">
            Manage Subscription
          </p>
          <div className="mb-3 grid grid-cols-2 gap-3">
            <div>
              <p className="text-[11px] font-semibold text-muted-foreground">Started</p>
              <p className="text-xs font-bold text-foreground">
                {fmtDate(user?.stripe_subscription_period_start)}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-muted-foreground">
                {isCancelled ? "Access until" : "Renews on"}
              </p>
              <p className="text-xs font-bold text-foreground">
                {fmtDate(user?.stripe_subscription_period_end)}
              </p>
            </div>
          </div>

          {/* Active: show cancel button or inline confirm panel */}
          {isActive && (
            <>
              {!confirmCancel ? (
                <button
                  onClick={() => setConfirmCancel(true)}
                  className="w-full rounded-xl border border-border bg-card py-2 text-xs font-extrabold text-destructive transition hover:border-destructive/50 hover:bg-destructive/10"
                >
                  Cancel renewal
                </button>
              ) : (
                <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3">
                  <div className="mb-2.5 flex items-start gap-2">
                    <Warning size={14} weight="fill" className="mt-0.5 shrink-0 text-destructive" />
                    <p className="text-xs font-semibold leading-snug text-foreground">
                      Cancel renewal? You'll keep access until{" "}
                      <span className="font-bold">{fmtDate(user?.stripe_subscription_period_end)}</span>.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setConfirmCancel(false)}
                      disabled={cancelling}
                      className="rounded-lg border border-border bg-card py-1.5 text-xs font-extrabold text-foreground transition hover:bg-muted disabled:opacity-50"
                    >
                      Keep plan
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={cancelling}
                      className="rounded-lg bg-destructive py-1.5 text-xs font-extrabold text-white transition hover:bg-destructive/90 disabled:opacity-50"
                    >
                      {cancelling ? "Cancelling…" : "Yes, cancel"}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Already cancelled */}
          {isCancelled && (
            <p className="text-center text-[11px] font-semibold text-muted-foreground">
              Subscription cancelled — no further charges
            </p>
          )}
        </div>
      )}

      {/* ── Sign out ── */}
      <div className="px-2 py-2">
        <button
          onClick={onSignOut}
          className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-extrabold text-muted-foreground transition hover:bg-muted hover:text-destructive"
        >
          <SignOut size={15} weight="bold" />
          Sign out
        </button>
      </div>
    </div>
  );
}

/* ── VocabHeader ──────────────────────────────────────────────────────── */
export function VocabHeader({ onSubscribe }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);


  // Close on outside click
  useEffect(() => {
    function handler(e) {
      if (open && wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    function handler(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const initials  = getInitials(user?.name);
  const firstName = user?.name?.split(" ")[0] ?? "";

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-card/85 backdrop-blur">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-2.5 sm:px-6">

        {/* Logo */}
        <Link to="/vocab/children" className="flex items-center gap-2" data-testid="vocab-logo">
          <Mascot size={26} animated={false} />
          <span className="font-display text-base font-black tracking-tight text-foreground">
            Bloom Vocabulary
          </span>
        </Link>

        <nav className="flex items-center gap-1 sm:gap-2">
          {/* Nav links */}
          {[
            { to: "/vocab/children", label: "Children",  testid: "nav-children" },
            { to: "/vocab/parent",   label: "Dashboard", testid: "nav-parent-dashboard" },
          ].map(({ to, label, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                cn(
                  "rounded-full px-3.5 py-2 text-sm font-extrabold transition-colors",
                  isActive
                    ? "bg-primary/12 text-primary-strong"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )
              }
            >
              {label}
            </NavLink>
          ))}

          {/* Admin link */}
          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className="flex items-center gap-1 rounded-full px-3.5 py-2 text-sm font-extrabold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <GearSix size={15} weight="bold" />
              Admin
            </NavLink>
          )}

          {/* Profile button + dropdown */}
          <div className="relative" ref={wrapRef}>
            <button
              data-testid="profile-btn"
              aria-haspopup="true"
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className={cn(
                "flex items-center gap-1.5 rounded-full border py-1 pl-1 pr-2.5 transition",
                open
                  ? "border-primary/40 bg-primary/8 shadow-sm"
                  : "border-border hover:border-primary/30 hover:bg-muted",
              )}
            >
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-black text-primary-foreground">
                {initials}
              </div>
              <span className="hidden sm:inline max-w-[72px] truncate text-sm font-extrabold text-foreground">
                {firstName}
              </span>
              <CaretDown
                size={11}
                weight="bold"
                className={cn(
                  "text-muted-foreground transition-transform duration-200",
                  open && "rotate-180",
                )}
              />
            </button>

            {open && (
              <ProfileDropdown
                user={user}
                onSubscribe={() => {
                  setOpen(false);
                  if (onSubscribe) {
                    onSubscribe();
                  } else {
                    navigate("/vocab/parent");
                  }
                }}
                onSignOut={async () => {
                  setOpen(false);
                  await logout();
                  navigate("/vocab");
                }}
              />
            )}
          </div>
        </nav>
      </div>
    </header>
  );
}